import {
  IconBeach,
  IconChecklist,
  IconHome,
  IconMenu2,
  IconMessageCircle,
} from '@tabler/icons-react';
import { Link, useLocation } from 'react-router-dom';
import { canSeeApprovals } from '../access';
import { useChatOverview } from '../../api/chat';
import { usePendingApprovalsCount } from '../queries';
import { useSession } from '../session';
import classes from './shell.module.css';

interface BottomBarProps {
  menuOpen: boolean;
  onToggleMenu: () => void;
  /** Called when a link is followed so the drawer closes. */
  onNavigate: () => void;
}

/** Mobile bottom bar: Home · Approvals (managers) or Holiday (staff) · Chat · Menu. */
export function BottomBar({ menuOpen, onToggleMenu, onNavigate }: BottomBarProps) {
  const { access, signedIn } = useSession();
  const { pathname } = useLocation();
  const chatUnread = useChatOverview(signedIn).data?.totalUnread ?? 0;
  const onChat = pathname.startsWith('/chat');
  const pending = usePendingApprovalsCount();
  const onHoliday = pathname.startsWith('/hr/holiday');
  const canApprove = canSeeApprovals(access);

  return (
    <nav aria-label="Main" className={classes.bottomBar}>
      <Link
        to="/"
        className={classes.bottomItem}
        data-active={(pathname === '/' && !menuOpen) || undefined}
        onClick={onNavigate}
      >
        <IconHome size={22} stroke={2} />
        Home
      </Link>
      <Link
        to={canApprove ? '/hr/holiday/approvals' : '/hr/holiday'}
        className={classes.bottomItem}
        data-active={(onHoliday && !menuOpen) || undefined}
        onClick={onNavigate}
      >
        {canApprove ? <IconChecklist size={22} stroke={2} /> : <IconBeach size={22} stroke={2} />}
        {canApprove ? 'Approvals' : 'Holiday'}
        {canApprove && pending > 0 && (
          <span className={classes.bottomBadge} aria-label={`${pending} waiting`}>
            {pending}
          </span>
        )}
      </Link>
      <Link
        to="/chat"
        className={classes.bottomItem}
        data-active={(onChat && !menuOpen) || undefined}
        aria-label={chatUnread ? `Chat, ${chatUnread} unread` : 'Chat'}
        onClick={onNavigate}
      >
        <IconMessageCircle size={22} stroke={2} />
        Chat
        {chatUnread > 0 && (
          <span className={classes.bottomBadge} aria-hidden="true">
            {chatUnread > 99 ? '99+' : chatUnread}
          </span>
        )}
      </Link>
      <button
        type="button"
        className={classes.bottomItem}
        data-active={menuOpen || undefined}
        aria-label="Menu"
        aria-expanded={menuOpen}
        onClick={onToggleMenu}
      >
        <IconMenu2 size={22} stroke={2} />
        Menu
      </button>
    </nav>
  );
}
